import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';
import { useTransactions } from '#hooks/useTransactions';
import { uncategorizedTransactions } from '#queries';

import { ingresosGastosPorMes, resumenAhorro } from './calculos';
import { coloresDeSeries } from './coloresCategorias';
import {
  Cargando,
  etiquetaMesCorta,
  FilaValor,
  formatPorcentaje,
  GUTTER,
  Hero,
  PaginaInforme,
  Punto,
  Seccion,
} from './comunes';
import { BarrasMensuales } from './graficas';
import type { PuntoMes } from './graficas';
import { useCategoriasExcluidas } from './useCategoriasExcluidas';
import { useTotalesMensuales } from './useTotalesMensuales';

// Dos series: paleta categórica (B4 · H47).
export const [COLOR_INGRESOS, COLOR_GASTOS] = coloresDeSeries(2);

/**
 * Frase de la tasa de ahorro de un mes completo (H84). «Gastaste más» sale del
 * ahorro real, porque la tasa está acotada a 0-100 %.
 */
export function fraseAhorro(
  t: (key: string, opts?: Record<string, unknown>) => string,
  tasa: number | null,
  ahorro: number,
  format: (v: number) => string,
  nombreMes: string,
): { frase: string; tono: 'bien' | 'mal' | 'neutro' } {
  if (tasa === null) {
    return {
      frase: t('No income recorded in {{month}}.', { month: nombreMes }),
      tono: 'neutro',
    };
  }
  if (ahorro < 0) {
    return {
      frase: t('In {{month}} you spent {{amount}} more than you earned.', {
        month: nombreMes,
        amount: format(-ahorro),
      }),
      tono: 'mal',
    };
  }
  return {
    frase: t('In {{month}} you saved {{pct}} of your income ({{amount}}).', {
      month: nombreMes,
      pct: formatPorcentaje(tasa, false),
      amount: format(ahorro),
    }),
    tono: tasa >= 0.1 ? 'bien' : 'neutro',
  };
}

/** Línea neutra del mes en curso: «Este mes, hasta hoy (día 4): 12 %». */
export function fraseEnCurso(
  t: (key: string, opts?: Record<string, unknown>) => string,
  tasa: number | null,
  dia: number,
): string {
  return tasa === null
    ? t('This month so far (day {{day}}): no income yet', { day: dia })
    : t('This month so far (day {{day}}): {{pct}}', {
        day: dia,
        pct: formatPorcentaje(tasa, false),
      });
}

/** Día del mes de hoy (1-31). */
export function diaDeHoy(): number {
  return Number(monthUtils.currentDay().slice(8, 10));
}

/**
 * Aviso «Revisa N movimientos sin categoría» (H84): mientras haya entradas o
 * salidas sin categoría, la tasa no es fiable. Lleva a Revisar.
 */
export function AvisoSinCategoria() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const consulta = useMemo(() => uncategorizedTransactions().select('*'), []);
  const { transactions, isPending } = useTransactions({
    query: consulta,
    options: { pageSize: 1000 },
  });
  const numero = isPending ? 0 : transactions.length;
  if (numero === 0) {
    return null;
  }
  return (
    <Button
      variant="bare"
      onPress={() => void navigate('/revisar')}
      data-testid="aviso-sin-categoria"
      style={{
        justifyContent: 'flex-start',
        padding: 0,
        ...styles.smallText,
        color: theme.warningText,
        fontWeight: 600,
      }}
    >
      {t(
        'Review {{count}} uncategorized transactions: the rate does not count them yet',
        { count: numero },
      )}
    </Button>
  );
}

/** Ingresos frente a gastos, 12 meses, con la tasa de ahorro de cada mes. */
export function IngresosGastosPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const format = useFormat();
  const { excluidas } = useCategoriasExcluidas();
  const { movimientos, categorias, meses, isLoading } = useTotalesMensuales({
    meses: 12,
    categoriasExcluidas: excluidas,
  });

  const porMes = ingresosGastosPorMes(movimientos, meses, categorias);
  // H84: la cifra principal es la del último mes completo; la del mes en
  // curso, aparte y en neutro (a principio de mes sale inflada).
  const { cerrado, enCurso, habitual: tasaHabitual } = resumenAhorro(porMes);
  const nombreCerrado = cerrado
    ? monthUtils.format(cerrado.mes, 'MMMM', locale)
    : '';
  const { frase, tono } = cerrado
    ? fraseAhorro(
        t,
        cerrado.tasa,
        cerrado.ahorro,
        v => format(v, 'financial'),
        nombreCerrado,
      )
    : { frase: '', tono: 'neutro' as const };

  const datos: PuntoMes[] = porMes.map(m => ({
    mes: m.mes,
    etiqueta: etiquetaMesCorta(m.mes, locale),
    ingresos: m.ingresos,
    gastos: m.gastos,
  }));
  const series = [
    { clave: 'ingresos', color: COLOR_INGRESOS, nombre: t('Income') },
    { clave: 'gastos', color: COLOR_GASTOS, nombre: t('Spending') },
  ];

  return (
    <PaginaInforme
      titulo={t('Income vs spending')}
      data-testid="informe-ingresos-gastos"
    >
      <View style={{ padding: GUTTER, gap: 14 }}>
        {isLoading ? (
          <Cargando />
        ) : (
          <Hero
            etiqueta={t('Savings rate in {{month}}', { month: nombreCerrado })}
            valor={
              cerrado?.tasa == null
                ? '—'
                : formatPorcentaje(cerrado.tasa, false)
            }
            frase={
              tasaHabitual !== null
                ? `${frase} ${t('Your usual rate is {{pct}}.', {
                    pct: formatPorcentaje(tasaHabitual, false),
                  })}`
                : frase
            }
            tono={tono}
          />
        )}
        {!isLoading && enCurso && (
          <Text
            style={{ ...styles.smallText, color: theme.pageTextSubdued }}
            data-testid="ahorro-en-curso"
          >
            {fraseEnCurso(t, enCurso.tasa, diaDeHoy())}
          </Text>
        )}
        <AvisoSinCategoria />
      </View>
      {!isLoading && (
        <>
          <View style={{ paddingLeft: 4, paddingRight: 8 }}>
            <BarrasMensuales
              datos={datos}
              series={series}
              formato={v => format(v, 'financial')}
              extraTooltip={p => {
                const m = porMes.find(x => x.mes === p.mes);
                return m && m.tasa !== null ? (
                  <div style={{ marginTop: 4, ...styles.smallText }}>
                    {t('Saved {{pct}}', {
                      pct: formatPorcentaje(m.tasa, false),
                    })}
                  </div>
                ) : null;
              }}
            />
          </View>
          <View
            style={{
              flexDirection: 'row',
              gap: 16,
              paddingLeft: GUTTER,
              paddingTop: 4,
            }}
          >
            {series.map(s => (
              <View
                key={s.clave}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
              >
                <View
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    backgroundColor: s.color,
                  }}
                />
                <Text style={styles.smallText}>{s.nombre}</Text>
              </View>
            ))}
          </View>
          <Seccion titulo={t('Month by month')}>
            {[...porMes]
              .reverse()
              .filter(m => m.ingresos !== 0 || m.gastos !== 0)
              .map(m => (
                <FilaValor
                  key={m.mes}
                  nombre={
                    <Text style={{ textTransform: 'capitalize' }}>
                      {monthUtils.format(m.mes, 'MMMM yyyy', locale)}
                    </Text>
                  }
                  detalle={
                    m.tasa === null
                      ? t('no income')
                      : t('saved {{pct}}', {
                          pct: formatPorcentaje(m.tasa, false),
                        })
                  }
                  icono={
                    <Punto
                      color={m.ahorro >= 0 ? COLOR_INGRESOS : COLOR_GASTOS}
                    />
                  }
                  valor={format(m.ahorro, 'financial')}
                  tonoValor={m.ahorro < 0 ? 'mal' : 'neutro'}
                  valorSecundario={t('{{income}} in · {{spending}} out', {
                    income: format(m.ingresos, 'financial'),
                    spending: format(m.gastos, 'financial'),
                  })}
                />
              ))}
          </Seccion>
        </>
      )}
    </PaginaInforme>
  );
}
